import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Avatar,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Add,
  Today,
  NavigateBefore,
  NavigateNext,
  Block,
  PictureAsPdf,
  Search,
  EditCalendar,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import {
  format,
  startOfMonth,
  addMonths,
  subMonths,
  parseISO,
} from 'date-fns';
import { dateTimeUtils } from '../../utils/dateTimeUtils';

import { useAuth } from '../../context/AuthContext';
import { useTimezone } from '../../hooks/useTimezone';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import employeeAttendanceService, { EmployeeAttendanceService } from '../../services/employeeAttendanceService';
import { centerService } from '../../services/centerService';
import { userService } from '../../services/userService';
import {
  Center,
  AttendanceStatus,
  InformationType,
} from '../../types';
import MarkAttendanceModal, { SavePayload } from '../../components/attendance/MarkAttendanceModal';
import AttendanceMarkBar from '../../components/attendance/AttendanceMarkBar';
import AttendanceCellPalette, { CellPaletteValue } from '../../components/attendance/AttendanceCellPalette';
import AttendanceRegisterDocument from '../../components/attendance/pdf/AttendanceRegisterDocument';
import BusyIcon from '../../components/common/BusyIcon';
import { STATUS_VISUAL, INFO_DOT } from '../../utils/attendanceStatus';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';

// Enhanced types for the employee attendance
interface DayAttendanceData {
  date: string;
  attendanceRecords: {
    [userId: string]: {
      status: AttendanceStatus;
      informationType?: InformationType;
      remarks?: string;
    };
  };
  isScheduledOff?: boolean;
}

interface MonthlyAttendanceData {
  school: {
    _id: string;
    name: string;
  };
  employees: Array<{
    _id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
  }>;
  days: DayAttendanceData[];
  year: number;
  month: number;
}

// ---------------------------------------------------------------------------
// Mark Mode (inline column editing) — mirrors the student page
// ---------------------------------------------------------------------------
// Drafts are committed in a single markBulkAttendance call when the user
// hits Save in the floating MarkBar. `saved*` fields are a snapshot taken
// on edit-mode entry so we can compute and warn before overwriting.
interface DraftEntry {
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
  savedStatus?: AttendanceStatus;
  savedInfoType?: InformationType;
  savedRemarks?: string;
  hadSavedRecord: boolean;
  // User explicitly chose "Not marked" for this employee on the active day.
  // Save either soft-deletes the existing record (if any) or drops the entry
  // entirely (net-new).
  cleared?: boolean;
}

interface PaletteAnchor {
  el: HTMLElement;
  userId: string;
  title: string;
  subtitle?: string;
}

const CYCLE_ORDER: AttendanceStatus[] = [
  AttendanceStatus.PRESENT,
  AttendanceStatus.ABSENT,
  AttendanceStatus.LATE,
  AttendanceStatus.HALF_DAY
];

const NEEDS_INFO_TYPE = new Set<AttendanceStatus>([
  AttendanceStatus.LATE,
  AttendanceStatus.HALF_DAY,
  AttendanceStatus.ABSENT
]);

// Statuses that come from the server overlay (holidays) or other systems
// (leave management, prior bulk day-off marks) and shouldn't be flipped by
// the per-employee inline cycle. Cells with these statuses are read-only
// while in Mark Mode — same spirit as the student page's isOverlay check.
const NON_INLINE_EDITABLE_STATUSES = new Set<AttendanceStatus>([
  AttendanceStatus.SCHEDULED_OFF,
  AttendanceStatus.NOT_SCHEDULED,
  AttendanceStatus.HOLIDAY,
  AttendanceStatus.LEAVE
]);

function nextStatusInCycle(current: AttendanceStatus): AttendanceStatus {
  const idx = CYCLE_ORDER.indexOf(current);
  return CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
}

const EmployeeAttendancePage: React.FC = () => {
  const { user } = useAuth();
  const { canRead, canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { isToday, formatTime, formatDateTime, getToday } = useTimezone();

  // State management
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState<Center[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [monthlyData, setMonthlyData] = useState<MonthlyAttendanceData | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Modal state
  const [markAttendanceModalOpen, setMarkAttendanceModalOpen] = useState(false);
  const [selectedModalDate, setSelectedModalDate] = useState<Date>(new Date());
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Mark Mode (inline column editing) state
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [draftMap, setDraftMap] = useState<Record<string, DraftEntry>>({});
  const [paletteAnchor, setPaletteAnchor] = useState<PaletteAnchor | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  // Permission checks using new system
  const canViewAttendance = canRead('employee_attendance');
  const canMarkAttendance = canWrite('employee_attendance');

  // Load centers on component mount
  useEffect(() => {
    const loadCenters = async () => {
      if (user?.isSystemAdmin || false) {
        try {
          const response = await centerService.getCenters();
          const centersData = response?.centers || [];
          setCenters(centersData);
        } catch (error) {
          console.error('Error loading centers:', error);
        }
      } else {
        if (user?.schoolId) {
          setSelectedCenter(user.schoolId._id);
        }
      }
    };
    loadCenters();
  }, [user]);

  // Load monthly attendance data - single API call
  const loadMonthlyAttendance = useCallback(async () => {
    if (!selectedCenter) return;

    try {
      setLoading(true);
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      // Use monthly API for the entire month with pagination and search
      const monthlyApiData = await employeeAttendanceService.getMonthlyAttendance(
        selectedCenter,
        year,
        month,
        page + 1,
        rowsPerPage,
        debouncedSearchQuery
      );

      // Get employees from paginated response
      let employees = monthlyApiData.employeeAttendanceData.map((item: any) => item.employee);
      
      // Update total count from pagination
      if (monthlyApiData.pagination) {
        setTotalCount(monthlyApiData.pagination.totalItems);
      }

      // Generate all days in the month
      const daysInMonth = dateTimeUtils.getDaysInMonth(year, month);
      const monthlyDays: DayAttendanceData[] = [];
      
      daysInMonth.forEach(dateStr => {
        const dayData: DayAttendanceData = {
          date: dateStr,
          attendanceRecords: {},
          isScheduledOff: false,
        };

        // Populate attendance from monthly data
        monthlyApiData.employeeAttendanceData.forEach((employeeData: any) => {
          const attendanceForDate = employeeData.attendance[dateStr];
          if (attendanceForDate) {
            dayData.attendanceRecords[employeeData.employee._id] = {
              status: attendanceForDate.status as any,
              informationType: attendanceForDate.informationType as any,
              remarks: attendanceForDate.remarks,
            };
          }
        });

        monthlyDays.push(dayData);
      });

      setMonthlyData({
        school: monthlyApiData.school,
        employees,
        days: monthlyDays,
        year,
        month
      });

    } catch (error: any) {
      console.error('Error loading monthly employee attendance:', error);
      enqueueSnackbar(error.message || 'Error loading attendance data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedCenter, currentMonth, page, rowsPerPage, debouncedSearchQuery, enqueueSnackbar]);

  useEffect(() => {
    loadMonthlyAttendance();
  }, [loadMonthlyAttendance]);

  // Navigate months
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => 
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    );
  };

  // Open mark attendance modal - NO FETCHING
  const openMarkAttendanceModal = (date?: Date) => {
    if (!selectedCenter) {
      enqueueSnackbar('Please select a school first', { variant: 'warning' });
      return;
    }
    const dateToUse = date || new Date();
    setSelectedModalDate(dateToUse);
    setMarkAttendanceModalOpen(true);
  };

  // Search handler for modal
  const handleEmployeeSearch = useCallback(async (query: string) => {
    if (!selectedCenter) return [];
    try {
      const response = await userService.getUsers({
        page: 1,
        limit: 20, // Reasonable limit for dropdown
        search: query,
        centerId: selectedCenter, // Fixed: Use centerId instead of schoolId
        isActive: true,
      });
      const users = response?.users || [];
      return users.map((u: any) => ({
        _id: u._id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        role: u.roleId?.name || 'Employee'
      }));
    } catch (error) {
      console.error('Error searching employees:', error);
      return [];
    }
  }, [selectedCenter]);

  // Save attendance from modal - Supports Apply All
  const handleSaveAttendance = async (payload: SavePayload) => {
    if (!selectedCenter || !selectedModalDate) return;

    try {
      const formattedDate = EmployeeAttendanceService.formatDateForAPI(selectedModalDate);

      const result = await employeeAttendanceService.markBulkAttendance({
        schoolId: selectedCenter,
        date: formattedDate,
        attendanceRecords: payload.attendanceRecords,
        applyToAll: payload.applyToAll,
        status: payload.applyToAll ? (payload.bulkStatus as any) : undefined,
        informationType: payload.applyToAll ? (payload.bulkInfoType as any) : undefined,
        remarks: payload.applyToAll ? payload.bulkRemarks : undefined,
      });
      
      const { summary } = result;
      if (summary.failed > 0) {
        if (summary.successful === 0) {
          enqueueSnackbar(`Failed to save attendance`, { variant: 'error' });
          throw new Error('Failed to save');
        } else {
          enqueueSnackbar(`Partially saved: ${summary.successful} successful, ${summary.failed} failed`, { variant: 'warning' });
        }
      } else {
          const msg = payload.applyToAll 
            ? `Attendance marked for all employees: ${summary.successful} records updated.` 
            : `Attendance saved successfully for ${summary.successful} employee(s)`;
        enqueueSnackbar(msg, { variant: 'success' });
      }
      
      await loadMonthlyAttendance();
    } catch (error: any) {
      console.error('Error saving attendance:', error);
      throw error;
    }
  };

  // -----------------------------------------------------------------
  // Mark Mode helpers
  // -----------------------------------------------------------------

  // Build a fresh draft for a single visible employee row. Rows whose
  // saved status is in NON_INLINE_EDITABLE_STATUSES (HOLIDAY, LEAVE,
  // SCHEDULED_OFF, NOT_SCHEDULED) are intentionally skipped — those are
  // server-computed or come from leave management and shouldn't be
  // overwritten with a per-employee inline click. Use the legacy modal
  // for bulk day-off marks.
  // `dayHasAnyRecord`: when the day already has real records for some
  // employees, missing rows should NOT default to Present. See the twin
  // logic in the student-attendance page for the full rationale.
  const buildDraftForRow = (
    existing: { status: AttendanceStatus; informationType?: InformationType; remarks?: string } | undefined,
    dayHasAnyRecord: boolean
  ): DraftEntry | null => {
    if (existing && NON_INLINE_EDITABLE_STATUSES.has(existing.status)) return null;
    const isBlank = !existing;
    const baseStatus = existing?.status ?? AttendanceStatus.PRESENT;
    return {
      status: baseStatus,
      informationType: existing?.informationType,
      remarks: existing?.remarks,
      savedStatus: existing?.status,
      savedInfoType: existing?.informationType,
      savedRemarks: existing?.remarks,
      hadSavedRecord: !!existing,
      cleared: isBlank && dayHasAnyRecord
    };
  };

  const exitEditMode = useCallback(() => {
    setEditingDate(null);
    setDraftMap({});
    setPaletteAnchor(null);
    setConfirmOverwriteOpen(false);
  }, []);

  const enterEditMode = useCallback((dateStr: string) => {
    if (!canMarkAttendance || !monthlyData) return;
    const dayData = monthlyData.days.find(d => d.date === dateStr);
    const dayHasAnyRecord = !!dayData && monthlyData.employees.some((employee) => {
      const cell = dayData.attendanceRecords[employee._id] as
        | { status?: AttendanceStatus }
        | undefined;
      return !!cell?.status;
    });
    const next: Record<string, DraftEntry> = {};
    for (const employee of monthlyData.employees) {
      const existing = dayData?.attendanceRecords[employee._id];
      const draft = buildDraftForRow(existing, dayHasAnyRecord);
      if (draft) next[employee._id] = draft;
    }
    if (Object.keys(next).length === 0) {
      enqueueSnackbar('No employees can be marked on this day.', { variant: 'info' });
      return;
    }
    setDraftMap(next);
    setEditingDate(dateStr);
  }, [canMarkAttendance, monthlyData, enqueueSnackbar]);

  // Keep drafts in sync when the user pages while in edit mode — newly
  // visible employees get a default (Present on an untouched day, or
  // "Not marked" on a day that already has records so we don't accidentally
  // overwrite the intentional partial state). Existing drafts are preserved
  // so the user doesn't lose work by hopping pages.
  useEffect(() => {
    if (!editingDate || !monthlyData) return;
    const dayData = monthlyData.days.find(d => d.date === editingDate);
    const dayHasAnyRecord = !!dayData && monthlyData.employees.some((employee) => {
      const cell = dayData.attendanceRecords[employee._id] as
        | { status?: AttendanceStatus }
        | undefined;
      return !!cell?.status;
    });
    setDraftMap(prev => {
      let mutated = false;
      const next = { ...prev };
      for (const employee of monthlyData.employees) {
        if (next[employee._id]) continue;
        const existing = dayData?.attendanceRecords[employee._id];
        const draft = buildDraftForRow(existing, dayHasAnyRecord);
        if (draft) {
          next[employee._id] = draft;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [editingDate, monthlyData]);

  // Bail out of Mark Mode when scope changes — drafts are tied to a single
  // (school, date) tuple and don't make sense after switching schools or
  // navigating to a different month.
  useEffect(() => {
    if (!editingDate) return;
    exitEditMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenter, currentMonth]);

  const cycleDraftStatus = useCallback((userId: string) => {
    setDraftMap(prev => {
      const cur = prev[userId];
      if (!cur) return prev;
      // Cycle order: Present → Absent → Late → Half Day → Not marked →
      // back to Present.
      if (cur.cleared) {
        return {
          ...prev,
          [userId]: {
            ...cur,
            cleared: false,
            status: AttendanceStatus.PRESENT,
            informationType: undefined
          }
        };
      }
      const isLastInCycle = cur.status === CYCLE_ORDER[CYCLE_ORDER.length - 1];
      if (isLastInCycle) {
        return {
          ...prev,
          [userId]: {
            ...cur,
            cleared: true,
            informationType: undefined,
            remarks: undefined
          }
        };
      }
      const nextStatus = nextStatusInCycle(cur.status);
      const needsInfo = NEEDS_INFO_TYPE.has(nextStatus);
      return {
        ...prev,
        [userId]: {
          ...cur,
          status: nextStatus,
          informationType: needsInfo ? (cur.informationType ?? InformationType.UNINFORMED) : undefined
        }
      };
    });
  }, []);

  const applyPaletteValue = useCallback((userId: string, value: CellPaletteValue) => {
    setDraftMap(prev => {
      const cur = prev[userId];
      if (!cur) return prev;
      return {
        ...prev,
        [userId]: {
          ...cur,
          status: value.status,
          informationType: value.informationType,
          remarks: value.remarks,
          // Applying a status resurrects a previously-cleared cell.
          cleared: false
        }
      };
    });
  }, []);

  // Mark this employee's cell as "Not marked" for the active day.
  const clearPaletteValue = useCallback((userId: string) => {
    setDraftMap(prev => {
      const cur = prev[userId];
      if (!cur) return prev;
      return {
        ...prev,
        [userId]: {
          ...cur,
          cleared: true,
          informationType: undefined,
          remarks: undefined
        }
      };
    });
  }, []);

  const markAllPresentInDraft = useCallback(() => {
    setDraftMap(prev => {
      const out: Record<string, DraftEntry> = {};
      for (const [id, entry] of Object.entries(prev)) {
        out[id] = { ...entry, status: AttendanceStatus.PRESENT, informationType: undefined };
      }
      return out;
    });
  }, []);

  const draftCounts = useMemo(() => {
    const base: Record<AttendanceStatus, number> = {
      [AttendanceStatus.PRESENT]:        0,
      [AttendanceStatus.LATE]:           0,
      [AttendanceStatus.HALF_DAY]:       0,
      [AttendanceStatus.ABSENT]:         0,
      [AttendanceStatus.SCHEDULED_OFF]:  0,
      [AttendanceStatus.NOT_SCHEDULED]:  0,
      [AttendanceStatus.LEAVE]:          0,
      [AttendanceStatus.HOLIDAY]:        0
    };
    for (const entry of Object.values(draftMap)) {
      if (entry.cleared) continue;
      base[entry.status] += 1;
    }
    return base;
  }, [draftMap]);

  const changedFromSaved = useMemo(() => {
    let n = 0;
    for (const entry of Object.values(draftMap)) {
      if (!entry.hadSavedRecord) continue;
      // "Not marked" on a previously-saved cell IS a change (it soft-deletes
      // the record on save) — count it toward the overwrite gate.
      if (entry.cleared) { n += 1; continue; }
      const statusChanged = entry.status !== entry.savedStatus;
      const infoChanged = (entry.informationType || undefined) !== (entry.savedInfoType || undefined);
      const remarksChanged = (entry.remarks || '') !== (entry.savedRemarks || '');
      if (statusChanged || infoChanged || remarksChanged) n += 1;
    }
    return n;
  }, [draftMap]);

  const editingDateLabel = useMemo(() => {
    if (!editingDate) return '';
    try {
      return format(parseISO(editingDate), 'EEE, d MMM yyyy');
    } catch {
      return editingDate;
    }
  }, [editingDate]);

  const performDraftSave = useCallback(async () => {
    if (!editingDate || !selectedCenter) return;
    try {
      setSavingDraft(true);
      // Two-way split: normal upserts vs. cleared entries that need to be
      // soft-deleted on the backend. Cleared-with-no-saved-record is a pure
      // no-op and dropped.
      const records: {
        userId: string;
        status: AttendanceStatus;
        informationType?: InformationType;
        remarks?: string;
      }[] = [];
      const removeUserIds: string[] = [];
      for (const [userId, entry] of Object.entries(draftMap)) {
        if (entry.cleared) {
          if (entry.hadSavedRecord) removeUserIds.push(userId);
          continue;
        }
        records.push({
          userId,
          status: entry.status,
          informationType: entry.informationType,
          remarks: entry.remarks
        });
      }
      const result = await employeeAttendanceService.markBulkAttendance({
        schoolId: selectedCenter,
        date: editingDate,
        attendanceRecords: records,
        removeUserIds: removeUserIds.length > 0 ? removeUserIds : undefined
      });
      const { summary } = result;
      if (summary.failed > 0 && summary.successful === 0) {
        enqueueSnackbar('Failed to save attendance', { variant: 'error' });
        return;
      }
      if (summary.failed > 0) {
        enqueueSnackbar(`Partially saved: ${summary.successful} ok, ${summary.failed} failed`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Attendance saved for ${summary.successful} employee(s).`, { variant: 'success' });
      }
      exitEditMode();
      await loadMonthlyAttendance();
    } catch (err: any) {
      console.error('Error saving draft attendance:', err);
      const apiMessage = err?.response?.data?.message;
      enqueueSnackbar(apiMessage || err?.message || 'Failed to save attendance', { variant: 'error' });
    } finally {
      setSavingDraft(false);
    }
  }, [editingDate, selectedCenter, draftMap, enqueueSnackbar, exitEditMode, loadMonthlyAttendance]);

  const handleSaveDraft = useCallback(() => {
    if (changedFromSaved > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    void performDraftSave();
  }, [changedFromSaved, performDraftSave]);

  const handleConfirmOverwrite = useCallback(() => {
    setConfirmOverwriteOpen(false);
    void performDraftSave();
  }, [performDraftSave]);

  // How a cell looks — letters and colours from the shared attendance palette.
  const getStatusDisplay = (status: AttendanceStatus | null, leaveType?: string) => {
    const v = status ? STATUS_VISUAL[status] : undefined;
    if (!v) return {
      text: '·',
      color: 'text.secondary',
      bgColor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider'
    };
    return {
      text: v.text,
      color: '#ffffff',
      bgColor: v.bg,
      border: `1px solid ${v.border}`,
      borderColor: v.border,
      ...(status === AttendanceStatus.LEAVE ? { leaveType } : {})
    };
  };

  // Memo'd from `currentMonth` alone — never from server data. This is what
  // keeps the date-header row stable across filter / search / page changes;
  // it only rebuilds when the user actually navigates to a different month.
  const monthDates = useMemo(
    () => dateTimeUtils.getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
    [currentMonth]
  );

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // The monthly register, drawn in the browser from every employee's month.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const downloadRegister = async () => {
    if (downloadingPDF || !selectedCenter) return;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const period = format(currentMonth, 'MMMM yyyy');
    setDownloadingPDF(true);
    try {
      await generatePdf(async () => {
        const m = await employeeAttendanceService.getMonthlyAttendanceAll(selectedCenter, year, month);
        const n = m.employeeAttendanceData.length;
        return (
          <AttendanceRegisterDocument
            title="Employee Attendance Report"
            details={[{ value: period }, { value: `${n} employee${n === 1 ? '' : 's'}` }]}
            subtitle={period}
            lead={[{ header: 'Employee', strong: true }, { header: 'Role', width: 84 }]}
            rows={m.employeeAttendanceData.map((r: any) => ({ id: String(r.employee._id), cells: [r.employee.name, r.employee.role], attendance: r.attendance }))}
            dates={m.dates}
            withLeaves
            branding={branding}
            generatedAt={new Date().toISOString()}
          />
        );
      }, `employee-attendance-${period.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (!canViewAttendance) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="70vh"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <Block 
            sx={{ 
              fontSize: 56, 
              color: '#9e9e9e',
              mb: 2 
            }} 
          />
          <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Employee Attendance.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            Employee Attendance Management
          </Typography>
          
          <Box display="flex" gap={2}>
            {canViewAttendance && selectedCenter && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<BusyIcon busy={downloadingPDF} size={20}><PictureAsPdf /></BusyIcon>}
                onClick={downloadRegister}
                disabled={downloadingPDF}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {downloadingPDF ? 'Preparing…' : 'Download PDF'}
              </Button>
            )}
            {/* Add Attendance button hidden — inline column-header Mark Mode
                covers the daily flow. Kept commented because the modal's
                "Mark Off/Holiday" mode (SCHEDULED_OFF / NOT_SCHEDULED bulk)
                has no inline equivalent yet — those statuses live in
                NON_INLINE_EDITABLE_STATUSES. Restore once the inline bar
                grows a bulk day-off affordance. */}
            {/* {canMarkAttendance && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() => openMarkAttendanceModal()}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Add Attendance
              </Button>
            )} */}
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Search Bar */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Center Selection */}
              {(user?.isSystemAdmin || false) && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>School</InputLabel>
                    <Select
                      value={selectedCenter}
                      onChange={(e) => {
                        setSelectedCenter(e.target.value);
                        setMonthlyData(null);
                      }}
                      label="School"
                    >
                      {centers.map((center) => (
                        <MenuItem key={center._id} value={center._id}>
                          {center.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Month Navigation */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <IconButton onClick={() => navigateMonth('prev')} size="small">
                    <NavigateBefore />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center', fontSize: '0.8rem' }}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </Typography>
                  <IconButton onClick={() => navigateMonth('next')} size="small">
                    <NavigateNext />
                  </IconButton>
                  <Tooltip title="Go to current month">
                    <IconButton 
                      onClick={() => setCurrentMonth(startOfMonth(new Date()))}
                      size="small"
                      color="primary"
                    >
                      <Today />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
        </Paper>

        {/* No Selection State */}
        {!selectedCenter ? (
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select School
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select a school to view attendance data
              </Typography>
            </CardContent>
          </Card>
        ) : (
          /* Monthly Matrix — kept mounted across search / page / month
             changes so the header row and sticky employee column don't
             flicker. The only in-flight signal is the LinearProgress strip
             below and a faint dim on the rows while `loading` is true. */
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative'
          }}>
              <Box sx={{ height: 3, position: 'relative' }}>
                {loading && (
                  <LinearProgress
                    sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }}
                  />
                )}
              </Box>
              <CardContent sx={{ p: 0, opacity: loading ? 0.7 : 1, transition: 'opacity 150ms ease' }}>
              {monthlyData && monthlyData.employees && monthlyData.employees.length > 0 ? (
                <TableContainer 
                  sx={{ 
                    maxHeight: '75vh', 
                    overflowX: 'hidden',
                    '& .MuiTableCell-root': {
                      borderRight: (theme) => 
                        `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#e0e0e0'}`,
                      padding: '4px 2px'
                    }
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600, 
                            minWidth: 200, 
                            maxWidth: 200,
                            width: 200,
                            fontSize: '0.8rem',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: (theme) => 
                              theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
                              borderRight: (theme) => 
                                `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                              padding: '6px 12px'
                          }}
                        >
                          <Box sx={{ ml: 1 }}>
                            Employee
                          </Box>
                        </TableCell>
                        {monthDates.map((dateStr) => {
                          const displayDate = dateTimeUtils.formatDisplayDate(dateStr, { showDay: false });
                          const dayOfWeekIndex = dateTimeUtils.getDayOfWeek(dateStr);
                          const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                          const dayLabel = days[dayOfWeekIndex];

                          // "Today" follows the school timezone — comparing
                          // against UTC would highlight the wrong column for
                          // the hours of the day when PKT and UTC disagree.
                          const todayStr = getToday();
                          const isCurrentDay = dateStr === todayStr;
                          const isActiveColumn = editingDate === dateStr;
                          const headerClickable =
                            canMarkAttendance && !savingDraft && (!editingDate || isActiveColumn);
                          const headerTooltip = !canMarkAttendance
                            ? ''
                            : isActiveColumn
                              ? 'Exit edit mode (drafts will be discarded)'
                              : editingDate
                                ? 'Save or cancel the current column first'
                                : 'Click to mark attendance for this day';
                          const headerLabel = (
                            <TableCell
                              key={dateStr}
                              align="center"
                              onClick={headerClickable ? () => (isActiveColumn ? exitEditMode() : enterEditMode(dateStr)) : undefined}
                              sx={{
                                fontWeight: 600,
                                minWidth: isActiveColumn ? '38px' : '28px',
                                maxWidth: isActiveColumn ? '38px' : '28px',
                                width: isActiveColumn ? '38px' : '28px',
                                fontSize: '0.65rem',
                                cursor: headerClickable ? 'pointer' : 'default',
                                backgroundColor: (theme) => {
                                  if (isActiveColumn) {
                                    return theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.28)
                                      : alpha(theme.palette.primary.main, 0.14);
                                  }
                                  return isCurrentDay
                                    ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 1)' : 'grey.100'
                                    : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100';
                                },
                                borderBottom: (theme) =>
                                  isActiveColumn
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                                padding: '4px 1px',
                                transition: 'background-color 200ms ease, min-width 180ms ease, max-width 180ms ease, width 180ms ease',
                                '&:hover': headerClickable && !isActiveColumn ? {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.18)
                                      : alpha(theme.palette.primary.main, 0.08)
                                } : undefined
                              }}
                            >
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    fontSize: '0.55rem',
                                    lineHeight: 1,
                                    fontWeight: 600,
                                    color: (theme) =>
                                      isActiveColumn
                                        ? theme.palette.primary.main
                                        : isCurrentDay
                                          ? theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1976d2'
                                          : theme.palette.text.secondary
                                  }}
                                >
                                  {dayLabel}
                                </Typography>
                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.25} sx={{ mt: 0.25 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      lineHeight: 1,
                                      color: (theme) =>
                                        isActiveColumn
                                          ? theme.palette.primary.main
                                          : isCurrentDay
                                            ? theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1976d2'
                                            : theme.palette.text.primary
                                    }}
                                  >
                                    {displayDate}
                                  </Typography>
                                  {isActiveColumn && (
                                    <EditCalendar sx={{ fontSize: 12, color: 'primary.main' }} />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                          );
                          return headerTooltip
                            ? <Tooltip key={dateStr} title={headerTooltip} arrow placement="top">{headerLabel}</Tooltip>
                            : headerLabel;
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyData.employees.map((employee) => (
                        <TableRow 
                          key={employee._id}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: (theme) => 
                                theme.palette.mode === 'dark' ? 'grey.800' : '#f9f9f9' 
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: (theme) => 
                                `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#e0e0e0'}`
                            }
                          }}
                        >
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              backgroundColor: (theme) => 
                                theme.palette.mode === 'dark' ? 'grey.900' : 'white',
                              minWidth: 200,
                              maxWidth: 200,
                              width: 200,
                              borderRight: (theme) => 
                                `2px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'}`,
                              padding: '6px 12px'
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                                {employee.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight={500} 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {employee.name}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: '0.65rem',
                                    lineHeight: 1
                                  }}
                                >
                                  {employee.role}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          {monthDates.map((dateStr) => {
                            const dayData = monthlyData.days.find(d => d.date === dateStr);
                            const employeeAttendance = dayData?.attendanceRecords[employee._id] as any;

                            const isActiveColumn = editingDate === dateStr;
                            const isNonInlineEditable = employeeAttendance && NON_INLINE_EDITABLE_STATUSES.has(employeeAttendance.status);
                            const draft = isActiveColumn && !isNonInlineEditable ? draftMap[employee._id] : undefined;

                            // Pick display source: draft in active column, saved record otherwise.
                            // Cleared draft renders as empty regardless of any saved record.
                            const draftCleared = !!draft?.cleared;
                            const displayStatus = draftCleared
                              ? null
                              : (draft ? draft.status : employeeAttendance?.status);
                            const displayInfoType = draftCleared
                              ? undefined
                              : (draft ? draft.informationType : employeeAttendance?.informationType);

                            const statusDisplay = getStatusDisplay(
                              displayStatus || null,
                              draftCleared ? undefined : employeeAttendance?.leaveType
                            );

                            const todayStr = getToday();
                            const isCurrentDay = dateStr === todayStr;

                            // Did this draft change from the saved snapshot?
                            // A cleared draft on a previously-saved cell counts, since Save
                            // will soft-delete the existing record.
                            const draftChanged = !!draft && draft.hadSavedRecord && (
                              draft.cleared ||
                              draft.status !== draft.savedStatus ||
                              (draft.informationType || undefined) !== (draft.savedInfoType || undefined) ||
                              (draft.remarks || '') !== (draft.savedRemarks || '')
                            );

                            const activeEditable = isActiveColumn && !!draft;

                            // Tooltip suppressed in active column to keep interactions crisp;
                            // the palette popover surfaces remarks while editing.
                            const tooltipTitle = !isActiveColumn && (employeeAttendance?.remarks || employeeAttendance?.leaveType)
                              ? (employeeAttendance?.leaveType
                                  ? `On Leave: ${employeeAttendance.leaveType}${employeeAttendance.remarks ? ` - ${employeeAttendance.remarks}` : ''}`
                                  : employeeAttendance.remarks)
                              : '';

                            const openPaletteFor = (anchor: HTMLElement) => {
                              if (!draft) return;
                              setPaletteAnchor({
                                el: anchor,
                                userId: employee._id,
                                title: employee.name,
                                subtitle: employee.role
                              });
                            };

                            const handleCellClick = () => {
                              if (activeEditable) cycleDraftStatus(employee._id);
                            };

                            const handleCellContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
                              if (!activeEditable) return;
                              e.preventDefault();
                              openPaletteFor(e.currentTarget);
                            };

                            const cellSize = isActiveColumn ? 24 : 20;

                            const showInfoDot = !!displayStatus && !!displayInfoType &&
                              [AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(displayStatus);
                            const dotColor = displayInfoType === InformationType.INFORMED ? INFO_DOT.informed : INFO_DOT.uninformed;

                            const cellInner = (
                              <Box
                                onClick={activeEditable ? handleCellClick : undefined}
                                onContextMenu={activeEditable ? handleCellContextMenu : undefined}
                                sx={{
                                  width: cellSize,
                                  height: cellSize,
                                  borderRadius: 1,
                                  backgroundColor: statusDisplay.bgColor,
                                  color: statusDisplay.color,
                                  border: draftChanged ? '2px solid' : (statusDisplay.border || '1px solid'),
                                  borderColor: draftChanged ? '#f59e0b' : statusDisplay.borderColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  mx: 'auto',
                                  position: 'relative',
                                  cursor: activeEditable ? 'pointer' : (tooltipTitle ? 'help' : 'default'),
                                  transition: 'transform 120ms ease, box-shadow 120ms ease, width 150ms ease, height 150ms ease, border-color 150ms ease',
                                  ...(activeEditable && {
                                    boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`
                                  }),
                                  ...(activeEditable && {
                                    '&:hover': {
                                      transform: 'scale(1.12)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                      '& .cell-palette-pip': { opacity: 1, transform: 'scale(1)' }
                                    }
                                  })
                                }}
                              >
                                {statusDisplay.text}
                                {showInfoDot && (
                                  <Box sx={{
                                    position: 'absolute', top: -2, right: -2, width: 6, height: 6,
                                    borderRadius: '50%', backgroundColor: dotColor, border: '1px solid white'
                                  }} />
                                )}
                                {activeEditable && (
                                  <Tooltip title="More options (remarks, informed / uninformed)" arrow placement="top">
                                    <Box
                                      className="cell-palette-pip"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPaletteFor((e.currentTarget.parentElement as HTMLElement) || (e.currentTarget as HTMLElement));
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        bottom: -5,
                                        right: -5,
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        backgroundColor: 'background.paper',
                                        color: 'text.secondary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        opacity: 0,
                                        transform: 'scale(0.8)',
                                        transition: 'opacity 120ms ease, transform 120ms ease',
                                        boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.common.black, 0.18)}`,
                                        '&:hover': { color: 'primary.main', borderColor: 'primary.main' }
                                      }}
                                    >
                                      ⋯
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                            );

                            const wrapped = tooltipTitle
                              ? <Tooltip title={tooltipTitle} arrow placement="top">{cellInner}</Tooltip>
                              : cellInner;

                            return (
                              <TableCell
                                key={dateStr}
                                align="center"
                                sx={{
                                  backgroundColor: (theme) => {
                                    if (isActiveColumn) {
                                      return theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.main, 0.18)
                                        : alpha(theme.palette.primary.main, 0.08);
                                    }
                                    return isCurrentDay
                                      ? theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)'
                                      : 'inherit';
                                  },
                                  minWidth: isActiveColumn ? '38px' : '28px',
                                  maxWidth: isActiveColumn ? '38px' : '28px',
                                  width: isActiveColumn ? '38px' : '28px',
                                  padding: '2px 1px',
                                  transition: 'min-width 180ms ease, max-width 180ms ease, width 180ms ease, background-color 200ms ease'
                                }}
                              >
                                {wrapped}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : !loading ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Employees Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? 'No employees match your search criteria. Try adjusting your search.'
                      : 'No employees found for the selected school'}
                  </Typography>
                </Box>
              ) : (
                /* Initial fetch — hold the card height so the page doesn't
                   jump when rows arrive. */
                <Box sx={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              
              {/* Pagination */}
              {monthlyData && monthlyData.employees && monthlyData.employees.length > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                />
              )}
              
              {/* Legend */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2,
                justifyContent: 'center'
              }}>
                {(Object.keys(STATUS_VISUAL) as AttendanceStatus[]).map(st => (
                  <Box key={st} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, bgcolor: STATUS_VISUAL[st].bg, borderRadius: '50%' }} />
                    <Typography variant="caption">{STATUS_VISUAL[st].label} ({STATUS_VISUAL[st].text})</Typography>
                  </Box>
                ))}
                <Box display="flex" alignItems="center" gap={1} sx={{ ml: 2, borderLeft: '1px solid #ccc', pl: 2 }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: INFO_DOT.informed, borderRadius: '50%', border: '1px solid #ccc' }} />
                  <Typography variant="caption">Informed</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 6, height: 6, bgcolor: INFO_DOT.uninformed, borderRadius: '50%', border: '1px solid #ccc' }} />
                  <Typography variant="caption">Uninformed</Typography>
                </Box>
              </Box>
          </CardContent>
          </Card>
        )}

        <MarkAttendanceModal
          open={markAttendanceModalOpen}
          onClose={() => setMarkAttendanceModalOpen(false)}
          selectedDate={selectedModalDate}
          onDateChange={setSelectedModalDate}
          onSave={handleSaveAttendance}
          onSearch={handleEmployeeSearch}
        />

        {/* Inline Mark Mode — floating bar appears once a day header is
            clicked. Single Save / Cancel surface for the staged column. */}
        {editingDate && (
          <AttendanceMarkBar
            dateLabel={editingDateLabel}
            counts={draftCounts}
            totalDraftable={Object.keys(draftMap).length}
            changedFromSaved={changedFromSaved}
            saving={savingDraft}
            onMarkAllPresent={markAllPresentInDraft}
            onCancel={exitEditMode}
            onSave={handleSaveDraft}
          />
        )}

        {/* Per-cell palette — right-click or ⋯ pip on hover. Stages back
            into the page-level draftMap; never hits the server directly. */}
        <AttendanceCellPalette
          open={!!paletteAnchor}
          anchorEl={paletteAnchor?.el ?? null}
          title={paletteAnchor?.title ?? ''}
          subtitle={paletteAnchor?.subtitle}
          initial={(() => {
            if (!paletteAnchor) return { status: AttendanceStatus.PRESENT };
            const cur = draftMap[paletteAnchor.userId];
            return cur
              ? { status: cur.status, informationType: cur.informationType, remarks: cur.remarks }
              : { status: AttendanceStatus.PRESENT };
          })()}
          onClose={() => setPaletteAnchor(null)}
          onApply={(value) => {
            if (!paletteAnchor) return;
            applyPaletteValue(paletteAnchor.userId, value);
            setPaletteAnchor(null);
          }}
          onClear={() => {
            if (!paletteAnchor) return;
            clearPaletteValue(paletteAnchor.userId);
            setPaletteAnchor(null);
          }}
        />

        {/* Overwrite confirm — only opens when Save would replace one or
            more previously-saved records. Net-new marks skip this gate. */}
        <Dialog
          open={confirmOverwriteOpen}
          onClose={() => setConfirmOverwriteOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Overwrite saved attendance?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>{changedFromSaved}</strong> record{changedFromSaved === 1 ? '' : 's'} will replace values that were already saved for <strong>{editingDateLabel}</strong>. The audit trail keeps the old values, but this change is visible immediately.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmOverwriteOpen(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmOverwrite}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              Overwrite & Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeAttendancePage;
